import { WebSocketWrapper } from '@/composables/WebSocketWrapper'
import { computed, isRef, onMounted, onUnmounted, ref, type MaybeRef, type Ref } from 'vue'

const wsTimeout = 1000000
const wsReconnectDelay = 3000
const wsPingInterval = 20000
let wsPingHandle: number | undefined

import {
  type IncomingData,
  type SubscriptionRequest,
  PING_MESSAGE,
  type SubscriptionPayload,
  type MethodCall,
  type MethodCallback,
  type MethodCallMessage,
  type MethodResponses,
  type MethodNames,
  type MethodParams,
} from '@/types/WebsocketApi'
import { useQuery } from '@tanstack/vue-query'

import { timeoutManager } from '@tanstack/vue-query'
import stringify from 'json-stable-stringify'

function stableStringify(obj: unknown): string {
  return stringify(obj) || ''
}

///////////////////////////////////////////////////////////////////////
// WebSocket connection management and raw sending/receiving logic
///////////////////////////////////////////////////////////////////////

let websocket: WebSocketWrapper | null = null
const websocketAvailable = ref(false)

function onConnect() {
  console.log('WebSocket connected')
  if (subscribedResourcesMap.size > 0) {
    sendSubscribeMessage(Array.from(subscribedResourcesMap.keys()).map((key) => JSON.parse(key) as SubscriptionRequest))
  }
  if (wsPingInterval) {
    wsPingHandle = window.setInterval(() => {
      websocket?.send(PING_MESSAGE)
    }, wsPingInterval)
  }
  websocketAvailable.value = true
}

function onReceive(event: MessageEvent) {
  const message: IncomingData = JSON.parse(event.data) //TODO - validate message structure
  // console.log('WebSocket message received', message)

  if ('type' in message && message.type === 'subscribedData' && Array.isArray(message.data)) {
    // SUBSCRIPTION DATA MESSAGE
    // console.log('Processing subscription data message', message)
    message.data.forEach((payload) => {
      const resource: { id: string; params?: object } = { id: payload.id }
      if ('params' in payload && payload.params && Object.keys(payload.params).length !== 0) {
        resource.params = payload.params
      }

      const resourceKey = stableStringify(resource)
      if (subscribedResourcesMap.has(resourceKey)) {
        // console.log('Updating cache for resource', resource, 'with value', payload.value)
        retrieveSubscriptionFromCacheByKey(resourceKey).value = payload.value
      } else {
        console.warn('Received data for unsubscribed resource', resource)
      }
    })
    // } else if (message?.type === 'methodResult' && 'id' in message && 'data' in message) {
  } else if ('id' in message && 'result' in message) {
    // METHOD RESPONSE MESSAGE
    message.data = message.result
    // console.log('Processing method response', message)
    const callback = methodHandlerMap.get(message.id)
    if (callback) {
      callback(message)
      methodHandlerMap.delete(message.id)
    }
  } else if ('acknowledgedSubscriptions' in message) {
    //TODO - handle acknowledged subscriptions
  } else if ('type' in message && message.type === 'pong') {
    //Ignore pong messages, we just use them to keep the connection alive
  } else {
    //UNRECOGNIZED MESSAGE
    console.warn('Received message with unrecognized format', message)
  }
}

function onError(event: Event) {
  clearInterval(wsPingHandle)
  console.error('WebSocket error', event)
}

function onClose(event: Event) {
  clearInterval(wsPingHandle)
  console.log('WebSocket closed', event)
  websocketAvailable.value = false
}

export async function openWebSocket() {
  if (websocket === null) {
    websocket = new WebSocketWrapper({
      url: 'wss://ws.xair.cloud',
      timeout: wsTimeout,
      reconnectDelay: wsReconnectDelay,
      onReceive,
      onConnect,
      onError,
      onClose,
    })
    await websocket.init()
    console.log('WebSocket initialized')
  }
}

export function closeWebSocket() {
  websocket?.close()
  websocket = null
  console.log('WebSocket closed and instance reset')
}

export function reconnectWebSocket() {
  console.log('Reconnecting WebSocket...')
  websocket?.manualReconnect()
}

//TODO - send strings?
function sendSubscribeMessage(resources: SubscriptionRequest[]) {
  if (websocket?.getState() === WebSocket.OPEN) {
    websocket?.send({ action: 'subscribe', resources })
  }
}

//TODO - send strings?
function sendUnsubscribeMessage(resources: SubscriptionRequest[]) {
  if (websocket?.getState() === WebSocket.OPEN) {
    websocket?.send({ action: 'unsubscribe', resources })
  }
}

///////////////////////////////////////////////////////////////////////
// Subscription cache management
///////////////////////////////////////////////////////////////////////
const MaxCacheAge = 5 * 60 * 1000 //5 minutes
const SubscriptionCacheCheckInterval = 5 * 1000 //1 minute
const SubscriptionSyncInterval = 100 //100ms

//unsubscribedTimestamp == 0 means currently subscribed, otherwise it holds the timestamp of when it was unsubscribed, used for clearing old cache entries
//TODO - use indexedDB or similar so it survives page reloads?
const subscriptionsCache = new Map<string, { value: Ref<SubscriptionPayload['value'] | null>; unsubscribedTimestamp: number }>()

function retrieveSubscriptionFromCache(resource: SubscriptionRequest): Ref<SubscriptionPayload['value'] | null> {
  const key = stableStringify(resource)
  return retrieveSubscriptionFromCacheByKey(key)
}

function retrieveSubscriptionFromCacheByKey(resourceKey: string): Ref<SubscriptionPayload['value'] | null> {
  if (!subscriptionsCache.has(resourceKey)) {
    subscriptionsCache.set(resourceKey, { value: ref(null), unsubscribedTimestamp: 0 })
  }
  return subscriptionsCache.get(resourceKey)!.value
}

function markSubscriptionInCacheAsUnsubscribed(resourceKey: string) {
  if (subscriptionsCache.has(resourceKey)) {
    // console.log('Marked resource as unsubscribed, setting unsubscribed timestamp', resourceKey)
    subscriptionsCache.get(resourceKey)!.unsubscribedTimestamp = Date.now()
  }
}

function markSubscriptionInCacheAsSubscribed(resourceKey: string) {
  if (subscriptionsCache.has(resourceKey)) {
    if (subscriptionsCache.get(resourceKey)!.unsubscribedTimestamp !== 0) {
      // console.log('Marked resource as subscribed again, resetting unsubscribed timestamp', resourceKey)
    }
    subscriptionsCache.get(resourceKey)!.unsubscribedTimestamp = 0
  }
}

function clearOldSubscriptionCacheEntries() {
  const now = Date.now()
  subscriptionsCache.forEach((entry, key) => {
    if (entry.unsubscribedTimestamp !== 0 && now - entry.unsubscribedTimestamp > MaxCacheAge) {
      subscriptionsCache.delete(key)
      console.log('Cleared old cache entry for resource', key)
    }
  })
}

//cleared on logout
export function clearSubscriptionCache() {
  console.log('Clearing subscription cache')
  subscriptionsCache.clear()
}

///////////////////////////////////////////////////////////////////////
// Subscription functions
///////////////////////////////////////////////////////////////////////

type SubscriptionMap = Map<string, number>
const pendingSubsciptionsChanges: SubscriptionMap = new Map()
const subscribedResourcesMap: SubscriptionMap = new Map()

// syncing subscribed resources does not depend on websocket connection,
// we just want to pack them together and send subscribe/unsubscribe messages in bulk
window.setInterval(() => {
  //TODO - do not remove subscription that will be immediately re-added
  syncSubscribtions(pendingSubsciptionsChanges)
  pendingSubsciptionsChanges.clear()
}, SubscriptionSyncInterval)

//Clearing old cache entries every minute
//TODO - clear only if cache is too big or on disconnect
window.setInterval(() => {
  clearOldSubscriptionCacheEntries()
}, SubscriptionCacheCheckInterval)

function subscribeToResourceCached(resource: SubscriptionRequest) {
  const key = stableStringify(resource)
  const pendingSubscribers = pendingSubsciptionsChanges.get(key) || 0
  pendingSubsciptionsChanges.set(key, pendingSubscribers + 1)

  // console.log('Scheduled subscription for resource', resource)
}

function unsubscribeFromResourceCached(resource: SubscriptionRequest) {
  const key = stableStringify(resource)
  const pendingSubscribers = pendingSubsciptionsChanges.get(key) || 0
  pendingSubsciptionsChanges.set(key, pendingSubscribers - 1)

  // console.log('Scheduled unsubscription for resource', resource)
}

function syncSubscribtions(changes: SubscriptionMap) {
  const subscriptionsToAdd: SubscriptionRequest[] = []
  const subscriptionsToRemove: SubscriptionRequest[] = []
  changes.forEach((changeInSubscribers, key) => {
    if (changeInSubscribers === 0) {
      return
    }
    const currentSubscribersCount = subscribedResourcesMap.get(key) || 0
    const newSubscribersCount = currentSubscribersCount + changeInSubscribers
    if (newSubscribersCount > 0 && currentSubscribersCount <= 0) {
      subscriptionsToAdd.push(JSON.parse(key) as SubscriptionRequest)
      subscribedResourcesMap.set(key, newSubscribersCount)
    } else if (newSubscribersCount <= 0 && currentSubscribersCount > 0) {
      //if no more subscribers, add to unsubscribe array
      subscriptionsToRemove.push(JSON.parse(key) as SubscriptionRequest)
      subscribedResourcesMap.delete(key)
    }
  })

  if (subscriptionsToRemove.length > 0) {
    // console.log('Unsubscribing from resources', subscriptionsToRemove)
    sendUnsubscribeMessage(subscriptionsToRemove)
    for (const resource of subscriptionsToRemove) {
      markSubscriptionInCacheAsUnsubscribed(stableStringify(resource))
    }
  }

  if (subscriptionsToAdd.length > 0) {
    // console.log('Subscribing to resources', subscriptionsToAdd)
    sendSubscribeMessage(subscriptionsToAdd)
    for (const resource of subscriptionsToAdd) {
      markSubscriptionInCacheAsSubscribed(stableStringify(resource))
    }
  }
}

// function subscribeToResources(resources: SubscriptionMap) {
//   const subscriptionsToAdd: SubscriptionRequest[] = []

//   //add callbacks to map and aggregate new subscriptions to send in bulk
//   resources.forEach((newSubscribers, key) => {
//     let subscribersCount = subscribedResourcesMap.get(key) || 0
//     if (subscribersCount == 0) {
//       //if not already subscribed, add to subscribe message
//       subscriptionsToAdd.push(JSON.parse(key) as SubscriptionRequest)
//     }
//     subscribersCount += newSubscribers
//     subscribedResourcesMap.set(key, subscribersCount)
//   })

//   if (subscriptionsToAdd.length > 0) {
//     console.log('Subscribing to resources', subscriptionsToAdd)
//     sendSubscribeMessage(subscriptionsToAdd)
//     for (const resource of subscriptionsToAdd) {
//       markSubscriptionInCacheAsSubscribed(stableStringify(resource))
//     }
//   }
// }

// function unsubscribeFromResources(resources: SubscriptionMap) {
//   const subscriptionsToRemove: SubscriptionRequest[] = []

//   //remove callbacks from map and aggregate removed subscriptions to send in bulk
//   resources.forEach((removedSubscribers, key) => {
//     let subscribersCount = subscribedResourcesMap.get(key) || 0
//     subscribersCount -= removedSubscribers
//     if (subscribersCount <= 0) {
//       //if no more callbacks, add to unsubscribe array
//       subscriptionsToRemove.push(JSON.parse(key) as SubscriptionRequest)
//       subscribedResourcesMap.delete(key)
//     } else {
//       subscribedResourcesMap.set(key, subscribersCount)
//     }
//   })

//   if (subscriptionsToRemove.length > 0) {
//     console.log('Unsubscribing from resources', subscriptionsToRemove)
//     sendUnsubscribeMessage(subscriptionsToRemove)
//     for (const resource of subscriptionsToRemove) {
//       markSubscriptionInCacheAsUnsubscribed(stableStringify(resource))
//     }
//   }
// }

///////////////////////////////////////////////////////////////////////
// Method functions
///////////////////////////////////////////////////////////////////////

const methodHandlerMap = new Map<string, MethodCallback>()
function callMethod(messageId: string, resource: MethodCall, callback: MethodCallback) {
  // console.log('Calling method with resource', resource)
  const message: MethodCallMessage = {
    jsonrpc: '2.0',
    action: 'method',
    id: messageId,
    ...resource,
  }
  methodHandlerMap.set(messageId, callback)
  websocket?.send(message)
}

///////////////////////////////////////////////////////////////////////
// Vue composables for using subscription and method resources with caching
///////////////////////////////////////////////////////////////////////

export function useSubscriptionResource<IdType extends SubscriptionRequest['id']>(
  id: IdType,
  params?: Extract<SubscriptionRequest, { id: IdType }>['params'], //TODO - fix type
) {
  const resource = { id, params } as Extract<SubscriptionRequest, { id: IdType }>

  //TODO - try on Activated/Deactivated for keepalive
  onMounted(() => subscribeToResourceCached(resource))
  onUnmounted(() => unsubscribeFromResourceCached(resource))

  return retrieveSubscriptionFromCache(resource as SubscriptionRequest) as Ref<Extract<SubscriptionPayload, { id: IdType }>['value'] | null>
}

//params can be ref and changing them changes query and triggers new method call
export function useMethodResourceTanstack<MethodName extends MethodNames>(
  desc: { method: MethodName; params?: MaybeRef<MethodParams<MethodName>> },
  additionalOptions?: {
    enabled?: Ref<boolean>
    refetchInterval?: MaybeRef<number>
    staleTime?: MaybeRef<number | 'static'>
    callTimeout?: number
    onSuccess?: (data: MethodResponses[MethodName]['data']) => void
  },
) {
  // const resource = desc as Extract<MethodCall, { method: MethodName }>
  // console.log('Calling method for resource', resource)
  let messageId: string | null = crypto.randomUUID()

  const queryKey = [desc.method as string, desc.params as MaybeRef<object>] as const
  const queryFunction = async () => {
    // console.log('Calling request for resource', resource)

    return new Promise<MethodResponses[MethodName]['data']>((resolve, reject) => {
      const timeoutId = timeoutManager.setTimeout(() => {
        reject('Method call timed out')
        // console.log('Method call timed out for resource', resource)
      }, additionalOptions?.callTimeout || 5000)

      const callback: MethodCallback = (payload) => {
        messageId = null
        // console.log('Received payload for resource', resource, payload)

        // if (payload.method !== resource.method) {
        //   reject('Received method response does not match requested method')
        // }
        //TODO - validate payload data?
        timeoutManager.clearTimeout(timeoutId)
        resolve(payload.data as MethodResponses[MethodName]['data'])
      }
      if (messageId === null) {
        messageId = crypto.randomUUID()
      }
      callMethod(
        messageId,
        { method: desc.method, params: isRef(desc.params) ? desc.params.value : desc.params } as Extract<MethodCall, { method: MethodName }>,
        callback,
      )
    })
  }

  return useQuery({
    queryKey: queryKey,
    queryFn: queryFunction,
    staleTime: additionalOptions?.staleTime,
    enabled:
      additionalOptions?.enabled == undefined ? websocketAvailable : computed(() => additionalOptions?.enabled?.value && websocketAvailable.value),
    refetchInterval: additionalOptions?.refetchInterval,
  })
}
